import type { Identifier } from "ra-core";

import type {
  AdvisorUnavailability,
  Contact,
  Deal,
  DealActivityResult,
  DealNote,
  DealStageEntry,
  LeadAssignedEvent,
  LeadAssignment,
  LeadAssignmentEligibleAdvisor,
  LeadAssignmentMethod,
  LeadAssignmentReason,
  LeadAssignmentRule,
  LeadCadenceEvent,
  LeadCarouselNotice,
  ProjectGuardia,
  Sale,
  SalesProject,
  Task,
  TeamRosterEntry,
} from "../../types";
import { isProtectedLeadType } from "../../carouselTypes";

/**
 * Almikia Epic EL2 (TASK-073): shared types, constants and helpers of the pure BPO carousel
 * (`carouselEngine.ts`, `carouselCandidates.ts`, `carouselFollowups.ts`). Time zone
 * America/Mexico_City (UTC-6, no DST). No module-level mutable state.
 */
export const CAROUSEL_TIME_ZONE = "America/Mexico_City";
/** A guardia (and a new lead) starting at or after this time belongs to the afternoon block. */
export const AFTERNOON_BLOCK_START = "14:00:00";
export const PROTECTION_DAYS = 90;
export const DAY_MS = 24 * 60 * 60 * 1000;
export const HOUR_MS = 60 * 60 * 1000;

/** Reasons that advance the rotation; protection and manual rows never do. */
export const ROTATION_REASONS: readonly LeadAssignmentReason[] = [
  "lead_nuevo",
  "fallback_omision",
  "reasignacion_24h",
];
const BPO_LEAD_TYPES = ["bpo", "almikia"];

/** A lead as the carousel reads it; the owner may be missing (a new lead has none yet). */
export type CarouselDeal = Pick<
  Deal,
  | "id"
  | "project_id"
  | "lead_type"
  | "stage"
  | "contact_ids"
  | "created_at"
  | "archived_at"
> & { sales_id?: Identifier | null };
export type CarouselSale = Pick<
  Sale,
  "id" | "role" | "disabled" | "advisor_code" | "first_name"
>;

/** Everything the engine reads; the caller loads it, the engine never mutates it. */
export interface CarouselData {
  deals: readonly CarouselDeal[];
  contacts: readonly Pick<Contact, "id" | "phone_jsonb" | "sales_id">[];
  notes: readonly Pick<
    DealNote,
    "deal_id" | "date" | "activity_type_id" | "activity_result_id"
  >[];
  /** EL3: the open next-step tasks silence the cadence alert (`tasks.deal_id` set, not done). */
  tasks: readonly Pick<Task, "deal_id" | "done_date" | "due_date">[];
  /** EL3: resolves a touch's result key (a reply is any result but "no contestó" / "mensaje abierto"). */
  activityResults: readonly Pick<DealActivityResult, "id" | "key">[];
  stageEntries: readonly Pick<DealStageEntry, "deal_id" | "entered_at">[];
  rules: readonly LeadAssignmentRule[];
  eligible: readonly LeadAssignmentEligibleAdvisor[];
  sales: readonly CarouselSale[];
  salesProjects: readonly SalesProject[];
  roster: readonly Pick<
    TeamRosterEntry,
    "sales_id" | "advisor_code" | "departed_at"
  >[];
  log: readonly LeadAssignment[];
  guardias: readonly ProjectGuardia[];
  unavailability: readonly AdvisorUnavailability[];
  events: readonly LeadAssignedEvent[];
  notices: readonly LeadCarouselNotice[];
  /** EL3: the cadence outbox rows so far (only their ids are read, to number the new ones). */
  cadenceEvents: readonly Pick<LeadCadenceEvent, "id">[];
}

export interface CarouselRequest {
  dealId: Identifier;
  now: Date;
  /**
   * `lead_nuevo` (default), `manual` (the button), `reasignacion_24h` or `tope_reasignaciones`
   * (EL3 ping-pong cap: no rotation, straight to the Team Leader with a notice).
   */
  reason?: Extract<
    LeadAssignmentReason,
    "lead_nuevo" | "manual" | "reasignacion_24h" | "tope_reasignaciones"
  >;
  channel?: string;
  /** The failed advisor of a reassignment; never picked again. */
  excludeSalesId?: Identifier | null;
  /** Returns [0, 1). Only the weekend pick uses it; defaults to `Math.random`. */
  random?: () => number;
  assignedBy?: Identifier | null;
}

export interface CarouselResult {
  outcome: "assigned" | "no_candidates" | "already_assigned" | "skipped";
  sales_id: Identifier | null;
  method: Exclude<LeadAssignmentMethod, "manual"> | null;
  /** `lead_protegido` marks the skip of a proactivo / colega lead (EL3), like the SQL result. */
  reason: LeadAssignmentReason | "lead_protegido" | null;
}

/** The decision plus the new rows to append; `data` itself is untouched. */
export interface CarouselOutcome {
  result: CarouselResult;
  log: LeadAssignment[];
  events: LeadAssignedEvent[];
  notices: LeadCarouselNotice[];
  ownerChange: {
    deal_id: Identifier;
    sales_id: Identifier;
    previous_sales_id: Identifier | null;
  } | null;
}

export const sameId = (
  a: Identifier | null | undefined,
  b: Identifier | null | undefined,
) => a != null && b != null && String(a) === String(b);

export const normalizeClave = (clave: string | null | undefined) =>
  (clave ?? "").trim().toLowerCase();

export const toSeconds = (time: string) =>
  time.length === 5 ? `${time}:00` : time;

/** The next numeric id of a collection (ids the engine writes are explicit, never guessed). */
export const nextId = (rows: readonly { id: Identifier }[]) =>
  rows.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;

/** ISO weekday (1 = Monday .. 7), "YYYY-MM-DD" and "HH:MM:SS" of `now` in Mexico City. */
export const mexicoCityMoment = (now: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CAROUSEL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const part = (type: string) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "00";
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return {
    weekday: weekdays.indexOf(part("weekday")) + 1,
    date: `${part("year")}-${part("month")}-${part("day")}`,
    time: `${part("hour")}:${part("minute")}:${part("second")}`,
  };
};

/** Deterministic PRNG (mulberry32) for the injected `random`; the closure holds its own state. */
export const createSeededRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * A Mexican phone reduced to its 10 digits (strip non-digits, drop a leading 52 / 521); null
 * when it does not end up with exactly 10 digits. Same as `private.normalize_mx_phone()`.
 */
export const normalizeMxPhone = (phone: string | null | undefined) => {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("521")) return digits.slice(3);
  if (digits.length === 12 && digits.startsWith("52")) return digits.slice(2);
  return digits.length === 10 ? digits : null;
};

export const isBpoLead = (deal: Pick<CarouselDeal, "lead_type">) =>
  deal.lead_type != null && BPO_LEAD_TYPES.includes(deal.lead_type);

/** EL3: a proactivo / colega lead is never reassigned by any engine path. */
export const isProtectedLead = (deal: Pick<CarouselDeal, "lead_type">) =>
  isProtectedLeadType(deal.lead_type);

/** The person left the team: a departed roster row matches her id or her clave. */
export const hasDeparted = (
  sale: Pick<CarouselSale, "id" | "advisor_code">,
  roster: CarouselData["roster"],
): boolean => {
  const clave = normalizeClave(sale.advisor_code);
  return roster.some(
    (entry) =>
      entry.departed_at &&
      (sameId(entry.sales_id, sale.id) ||
        (clave !== "" && normalizeClave(entry.advisor_code) === clave)),
  );
};

export const isActiveMember = (
  data: Pick<CarouselData, "salesProjects">,
  salesId: Identifier,
  projectId: Identifier,
) =>
  data.salesProjects.some(
    (row) =>
      sameId(row.sales_id, salesId) &&
      sameId(row.project_id, projectId) &&
      !row.ended_at,
  );

/** Id of her latest rotation-advancing assignment on the project (-Infinity = never). */
export const lastRotationLogId = (
  log: CarouselData["log"],
  projectId: Identifier,
  salesId: Identifier,
) =>
  log
    .filter(
      (row) =>
        sameId(row.project_id, projectId) &&
        sameId(row.sales_id, salesId) &&
        row.outcome === "assigned" &&
        row.reason != null &&
        ROTATION_REASONS.includes(row.reason),
    )
    .reduce((max, row) => Math.max(max, Number(row.id)), -Infinity);
